import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SchoolSupplement } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { deepClone } from '@lx/shared/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputProduct, QuoteStore, QuoteToolLiteStore } from '../../store';
import { CardContainerComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { OperationKey } from '../../constants';

@Component({
  standalone: true,
  selector: 'lx-unaccompanied-minor',
  templateUrl: './unaccompanied-minor.component.html',
  styleUrls: ['./unaccompanied-minor.component.scss'],
  imports: [CommonModule, CardContainerComponent],
})
export class UnaccompaniedMinorComponent implements OnInit, OnChanges {
  @Input() unaccompaniedMinorServices: SchoolSupplement[] | undefined;

  price: Amount[] | undefined;
  endPrice: Amount[] | undefined;
  discount: string | undefined;

  selectedUnaccompaniedMinorRows = [];
  currency: Signal<string | undefined>;

  dataLayerEventName$: Observable<GoogleAnalyticsEventEnum> | undefined;

  protected loading: Signal<boolean | undefined>;

  private readonly dataLayerEventName = new BehaviorSubject<GoogleAnalyticsEventEnum>(undefined);

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.quoteStore.unaccompaniedMinorServices$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((quotedUMServices: QuoteOutputProduct[]) => {
      this.setPropertiesFromQuotedUMServices(quotedUMServices);
    });
    this.dataLayerEventName$ = this.dataLayerEventName.asObservable();
    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  ngOnChanges({ unaccompaniedMinorServices }: SimpleChanges): void {
    if (unaccompaniedMinorServices?.currentValue) {
      const totalPriceFromExtras = this.calculateTotalPrices(unaccompaniedMinorServices.currentValue, 'multiCurrencyPrice');
      this.endPrice = totalPriceFromExtras;
      this.price = totalPriceFromExtras;
    }
  }

  addRemoveUnaccompaniedMinor() {
    if (!this.selectedUnaccompaniedMinorRows?.length) {
      const unaccompaniedMinorRows = this.unaccompaniedMinorServices?.map((unaccompaniedMinor) => unaccompaniedMinor.row);
      this.quoteToolLiteStore.updateState({ selectedUnaccompaniedMinorRows: unaccompaniedMinorRows });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteUnaccompaniedMinorsAdd);
    } else {
      this.quoteToolLiteStore.updateState({ selectedUnaccompaniedMinorRows: undefined });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteUnaccompaniedMinorsRemove);
    }
  }

  private setPropertiesFromQuotedUMServices(quotedUMServices: QuoteOutputProduct[]): void {
    this.selectedUnaccompaniedMinorRows = quotedUMServices?.map((quotedUMServices) => quotedUMServices.row);
    this.endPrice = this.calculateTotalPrices(quotedUMServices, 'endPrice') || this.endPrice;
    this.price = this.calculateTotalPrices(quotedUMServices, 'price') || this.price;
    this.discount = quotedUMServices?.[0]?.discountTotalAsPercentage;
  }

  private calculateTotalPrices(unaccompaniedMinorServices: SchoolSupplement[] | QuoteOutputProduct[], key: 'multiCurrencyPrice' | 'endPrice' | 'price') {
    if (!unaccompaniedMinorServices?.length) {
      return undefined;
    }
    // Sum the prices of all unaccompanied minor services
    const totalPrice: Amount[] = [];
    unaccompaniedMinorServices.forEach((umService: SchoolSupplement | QuoteOutputProduct) => {
      const multiCurrencyPrice = umService[key];
      multiCurrencyPrice.forEach((price: Amount) => {
        const priceInTotalPrices: Amount = totalPrice.find((p) => p.currency === price.currency);
        if (!priceInTotalPrices) {
          totalPrice.push(deepClone(price));
        } else {
          priceInTotalPrices.value += price.value;
        }
      });
    });

    return totalPrice;
  }
}
